"use client";

import { ContactForm } from "@/components/sections/contact/ContactForm";
import { BrandMultiLineX, BrandWordmark } from "@/components/icons/SiteMarks";
import { useContent } from "@/components/site/ContentProvider";
import type { SitePageBand } from "@/content/schema";
import { officePhoneHref } from "@/lib/whatsapp";
import pageStyles from "./subpage.module.css";

type SubpageContactBandProps = {
  band: SitePageBand;
  headingId: string;
};

/**
 * Shared end-of-page block for `/services` + `/work` (ADR-020).
 * In-flow layout: CTA + form row → footer.
 * Must NOT use id="contact" — homepage spine `#contact{position:absolute}`
 * would pull the band out of flow and let the footer climb above it.
 */
export function SubpageContactBand({ band, headingId }: SubpageContactBandProps) {
  const site = useContent();
  const office = site.offices[0];
  const year = new Date().getFullYear();

  return (
    <section
      className={pageStyles.pageEnd}
      id="inquire"
      aria-labelledby={headingId}
    >
      <div className={pageStyles.bandRow}>
        <div className={pageStyles.bandCopy}>
          <h2 id={headingId} className={pageStyles.bandHeadline}>
            {band.headline}
          </h2>
          <p className={pageStyles.bandBody}>{band.body}</p>
        </div>

        <div className={pageStyles.bandForm}>
          <ContactForm scoped showHeadline={false} />
        </div>
      </div>

      <footer className={pageStyles.footer}>
        <div className={pageStyles.footerBrand}>
          <div className={pageStyles.footerLockup} aria-hidden>
            <BrandMultiLineX className={pageStyles.footerX} />
            <BrandWordmark className={pageStyles.footerMark} />
          </div>
          <span className={pageStyles.footerCopy} dir="ltr">
            © {year} Xattir
          </span>
        </div>
        {office ? (
          <div className={pageStyles.footerDetails}>
            <span className={pageStyles.footerLoc} dir="ltr">
              {office.code}
            </span>
            <a
              href={`mailto:${office.email}`}
              className={pageStyles.footerLink}
              dir="ltr"
            >
              {office.email}
            </a>
            {office.phones.slice(0, 2).map((phone) => (
              <a
                key={phone.href}
                href={officePhoneHref(phone, site.contact.whatsappMessage)}
                className={pageStyles.footerLink}
                dir="ltr"
                target="_blank"
                rel="noopener noreferrer"
              >
                {phone.label}
              </a>
            ))}
          </div>
        ) : null}
      </footer>
    </section>
  );
}
